import React, { useState } from 'react';
import { Award, Calendar, Eye, X, Star, BookOpen } from 'lucide-react';
import { certifications } from '../../mock/data';
import { useModalAccessibility } from '../../hooks/useModalAccessibility';
import './Certifications.css';

// Fallback local para imagem de certificado que não carrega.
// Data URI = sem dependência de serviço externo (o antigo via.placeholder.com saiu do ar).
const CERTIFICATE_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200">
      <rect width="300" height="200" fill="#26282E"/>
      <rect x="20" y="20" width="260" height="160" fill="none" stroke="#DAFF01" stroke-width="2" rx="8"/>
      <circle cx="150" cy="88" r="26" fill="none" stroke="#DAFF01" stroke-width="3"/>
      <path d="M138 112 L138 146 L150 138 L162 146 L162 112" fill="none" stroke="#DAFF01" stroke-width="3" stroke-linejoin="round"/>
      <text x="150" y="94" font-family="Inter, Arial, sans-serif" font-size="20" font-weight="700" fill="#DAFF01" text-anchor="middle">PZT</text>
    </svg>`
  );

const handleImageError = (event) => {
  // Evita loop caso o próprio fallback falhe.
  event.target.onerror = null;
  event.target.src = CERTIFICATE_FALLBACK;
};

const Certifications = ({ language }) => {
  const [selectedCertification, setSelectedCertification] = useState(null);

  const certificationsContent = {
    pt: {
      title: 'Certificações e Cursos',
      subtitle: 'Certificações que comprovam minha expertise e desenvolvimento profissional',
      viewCertificate: 'Ver Certificado',
      verifyCredential: 'Verificar Credencial',
      skillsLearned: 'Habilidades Adquiridas:',
      issuedBy: 'Emitido por:',
      completedIn: 'Concluído em:',
      close: 'Fechar',
      stats: {
        total: 'Total de Certificações',
        categories: 'Categorias'
      },
      categories: {
        'AI/ML': 'Inteligência Artificial',
        'Web Development': 'Desenvolvimento Web',
        'Backend': 'Backend',
        'Cloud': 'Computação em Nuvem',
        'Mobile': 'Desenvolvimento Mobile',
        'DevOps': 'DevOps',
        'Data Science': 'Ciência de Dados',
        'Security': 'Segurança',
        'Cybersecurity': 'Cibersegurança',
        'UI/UX': 'Design UI/UX',
        'Other': 'Outros'
      }
    },
    en: {
      title: 'Certifications and Courses',
      subtitle: 'Certifications that prove my expertise and professional development',
      viewCertificate: 'View Certificate',
      verifyCredential: 'Verify Credential',
      skillsLearned: 'Skills Learned:',
      issuedBy: 'Issued by:',
      completedIn: 'Completed in:',
      close: 'Close',
      stats: {
        total: 'Total Certifications',
        categories: 'Categories'
      },
      categories: {
        'AI/ML': 'Artificial Intelligence',
        'Web Development': 'Web Development',
        'Backend': 'Backend Development',
        'Cloud': 'Cloud Computing',
        'Mobile': 'Mobile Development',
        'DevOps': 'DevOps',
        'Data Science': 'Data Science',
        'Security': 'Information Security',
        'Cybersecurity': 'Cybersecurity',
        'UI/UX': 'UI/UX Design',
        'Other': 'Other'
      }
    }
  };

  const content = certificationsContent[language];

  const stats = {
    total: certifications.length,
    categories: [...new Set(certifications.map(c => c.category))].length
  };

  const openModal = (certification) => {
    setSelectedCertification(certification);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedCertification(null);
    document.body.style.overflow = 'unset';
  };

  const modalRef = useModalAccessibility(Boolean(selectedCertification), closeModal);



  return (
    <section id="certifications" className="certifications">
      <div className="container">
        <div className="certifications-header">
          <h2 className="section-title">{content.title}</h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </div>

        {/* Stats */}
        <div className="certifications-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <Award size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-number">{stats.total}</div>
              <div className="stat-label">{content.stats.total}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <BookOpen size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-number">{stats.categories}</div>
              <div className="stat-label">{content.stats.categories}</div>
            </div>
          </div>
        </div>

        {/* Certifications Grid */}
        <div className="certifications-grid">
          {certifications.map((certification) => {
            return (
              <div 
                key={certification.id} 
                className={`certification-card ${certification.featured ? 'featured' : ''}`}
              >
                {certification.featured && (
                  <div className="featured-badge">
                    <Star size={12} />
                    Featured
                  </div>
                )}
                
                <div className="certification-header">
                  <div className="certification-image">
                    <img
                      src={certification.image}
                      alt={certification.title}
                      onError={handleImageError}
                    />
                    <div className="certification-overlay">
                      <button 
                        className="view-btn"
                        onClick={() => openModal(certification)}
                      >
                        <Eye size={16} />
                        {content.viewCertificate}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="certification-content">
                  <div className="certification-category">
                    {content.categories[certification.category] || certification.category}
                  </div>
                  
                  <h3 className="certification-title">{certification.title}</h3>
                  
                  <p className="certification-description">
                    {certification.description[language]}
                  </p>

                  <div className="certification-meta">
                    <div className="certification-institution">
                      <Award size={14} />
                      <span>{certification.institution[language]}</span>
                    </div>
                    
                    <div className="certification-date">
                      <Calendar size={14} />
                      <span>{certification.date}</span>
                    </div>
                  </div>

                  <div className="certification-skills">
                    {certification.skills.slice(0, 3).map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                    {certification.skills.length > 3 && (
                      <span className="skill-tag more">
                        +{certification.skills.length - 3}
                      </span>
                    )}
                  </div>

                  <div className="certification-actions">
                    <button 
                      className="btn-primary"
                      onClick={() => openModal(certification)}
                    >
                      <Eye size={16} />
                      {content.viewCertificate}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Certificate Modal */}
        {selectedCertification && (
          <div className="certification-modal" onClick={closeModal}>
            <div
              className="modal-content"
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="certification-modal-title"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="modal-close"
                onClick={closeModal}
                aria-label={content.close}
              >
                <X size={20} />
              </button>

              <div className="modal-header">
                <h3 className="modal-title" id="certification-modal-title">{selectedCertification.title}</h3>
                <div className="modal-meta">
                  <span className="modal-institution">
                    {content.issuedBy} {selectedCertification.institution[language]}
                  </span>
                  <span className="modal-date">
                    {content.completedIn} {selectedCertification.date}
                  </span>
                </div>
              </div>

              <div className="modal-image">
                <img
                  src={selectedCertification.image}
                  alt={selectedCertification.title}
                  onError={handleImageError}
                />
              </div>

              <div className="modal-details">
                <div className="modal-description">
                  <p>{selectedCertification.description[language]}</p>
                </div>

                <div className="modal-skills">
                  <h4>{content.skillsLearned}</h4>
                  <div className="skills-list">
                    {selectedCertification.skills.map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Certifications;